import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { KeyValues, Notice, StatusPill, XmlView } from "../../design/components";
import { ApiError } from "../../api/client";
import {
  usePortalDocument,
  usePortalDocumentArchive,
  usePortalDocumentEvents,
  usePortalDocumentLegs,
  usePortalDocumentQr,
  usePortalDocumentValidation,
  usePortalDocumentXml,
  usePortalMe,
  usePortalReprocess,
} from "../../api/portal";
import type { DocumentDetail } from "../../api/types";
import { typeCodeLabel } from "../../domain/families";
import { DocTypeCell } from "../documents/DocTypeCell";
import { dateTime, omr } from "../../lib/format";
import { EventsBody } from "../documents/inspector/bodies/EventsBody";
import { OutcomeBody } from "../documents/inspector/bodies/OutcomeBody";
import { ValidationBody, ValidationSummary } from "../documents/inspector/bodies/ValidationBody";
import { StagePanel } from "../documents/inspector/StagePanel";
import { reprocessFailure } from "./AttentionList";
import { HelpdeskLink } from "./HelpdeskLink";
import { statePill } from "./PortalDocuments";
import { canReprocessRow, documentName, usePortalRole } from "./usePortal";
import styles from "./portal.module.css";

const DIRECTION_LABEL = { out: "Outbound", in: "Inbound" } as const;

function amount(value: number | null | undefined): string {
  return value === null || value === undefined ? "—" : omr(Number(value));
}

function Card({ id, title, children, aside }: { id: string; title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="card" aria-labelledby={`${id}-title`}>
      <div className="card-head">
        <h2 id={`${id}-title`} className="grow">
          {title}
        </h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

function XmlCard({ docId, doc }: { docId: string; doc: DocumentDetail }) {
  const built = doc.artifacts.some((artifact) => artifact.kind === "xml");
  const xml = usePortalDocumentXml(docId, built);
  return (
    <Card id="xml" title="XML">
      <div className="card-body">
        {!built ? (
          <Notice kind="info">The XML is built once the document passes mapping.</Notice>
        ) : xml.isError ? (
          <Notice kind="fail">The XML could not be loaded.</Notice>
        ) : !xml.data ? (
          <p className={styles.sub}>Loading…</p>
        ) : (
          <div className={styles.pane}>
            <XmlView src={xml.data} />
          </div>
        )}
      </div>
    </Card>
  );
}

function QrCard({ docId }: { docId: string }) {
  const qr = usePortalDocumentQr(docId);
  return (
    <Card id="qr" title="QR information">
      <div className="card-body">
        {qr.isLoading ? (
          <p className={styles.sub}>Loading…</p>
        ) : qr.error ? (
          <Notice kind="fail">The QR information could not be loaded.</Notice>
        ) : qr.url ? (
          <>
            <img className={styles.qr} src={qr.url} alt="QR information for this document" />
            <p className={styles.sub}>
              Printed on the invoice so the buyer can check the document against the hub record.
            </p>
          </>
        ) : (
          <Notice kind="info">No QR information for this document</Notice>
        )}
      </div>
    </Card>
  );
}

function ArchiveCard({ docId, doc }: { docId: string; doc: DocumentDetail }) {
  const sealed = doc.stage === "archived";
  const archive = usePortalDocumentArchive(docId, sealed);
  return (
    <Card id="archive" title="Archive">
      <div className="card-body">
        {!sealed ? (
          <Notice kind="info">This document is not sealed yet</Notice>
        ) : archive.isError ? (
          <Notice kind="fail">The archive record could not be loaded.</Notice>
        ) : !archive.data ? (
          <p className={styles.sub}>Loading…</p>
        ) : (
          <KeyValues
            pairs={[
              ["Sealed", dateTime(archive.data.sealedAt)],
              ["Sealed XML", <span className={styles.sha}>{archive.data.xmlSha256}</span>],
              ["Acknowledgement record", <span className={styles.sha}>{archive.data.aspAckSha256 ?? "—"}</span>],
              ["Seal version", archive.data.sealVersion],
            ]}
          />
        )}
      </div>
    </Card>
  );
}

function Actions({ doc }: { doc: DocumentDetail }) {
  const role = usePortalRole();
  const me = usePortalMe();
  const reprocess = usePortalReprocess();
  const [outcome, setOutcome] = useState<{ kind: "ok" | "fail"; text: string } | null>(null);
  const name = documentName(doc);

  function run() {
    setOutcome(null);
    reprocess.mutate(doc.id, {
      onSuccess: () => setOutcome({ kind: "ok", text: `${name} is being reprocessed.` }),
      onError: (error) => setOutcome({ kind: "fail", text: reprocessFailure(error) }),
    });
  }

  return (
    <Card id="actions" title="Actions">
      <div className="card-body">
        {outcome ? <Notice kind={outcome.kind}>{outcome.text}</Notice> : null}
        <div className={styles.actionRow}>
          {canReprocessRow(role, doc) ? (
            <button type="button" className="btn btn-sm" disabled={reprocess.isPending} onClick={run}>
              Reprocess
            </button>
          ) : null}
          <HelpdeskLink email={me.data?.helpdeskEmail ?? null} subject={`Document ${name}`} />
        </div>
      </div>
    </Card>
  );
}

/** "Credit note number", "Supplier invoice number"; "Document number" when the type is not known. */
function numberLabel(doc: DocumentDetail): string {
  return doc.typeCode === null ? "Document number" : `${typeCodeLabel(doc.typeCode, doc.direction)} number`;
}

/** The console Inspector's sections, fed from /api/portal/documents/{id} and its sub-resources. */
export function PortalDocumentDetail() {
  const { docId } = useParams();
  const document = usePortalDocument(docId);
  // a 404 detail must not trigger the sibling requests
  const loaded = document.isSuccess;
  const events = usePortalDocumentEvents(docId, loaded);
  const validation = usePortalDocumentValidation(docId, loaded);
  const legs = usePortalDocumentLegs(docId, loaded);

  if (document.isError) {
    const missing = document.error instanceof ApiError && document.error.status === 404;
    return (
      <>
        <h1 className="display">Document</h1>
        <Notice kind={missing ? "info" : "fail"}>
          <div>{missing ? "This document is not available" : "This document could not be loaded."}</div>
          <Link className="link" to="/portal/documents">
            Back to documents
          </Link>
        </Notice>
      </>
    );
  }

  const doc = document.data;
  if (!doc || !docId) return <div className="page-loading" />;
  const totals = doc.totals;

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{documentName(doc)}</h1>
          <div className={styles.headMeta}>
            <StatusPill {...statePill(doc.state)} />
            <span className={styles.sub}>
              {DIRECTION_LABEL[doc.direction]} ·{" "}
              <DocTypeCell typeCode={doc.typeCode} flags={doc.flags} direction={doc.direction} />
            </span>
          </div>
        </div>
      </div>

      <div className="split">
        <div className="grid">
          <Card id="details" title="Details">
            <div className="card-body">
              <KeyValues
                pairs={[
                  [numberLabel(doc), doc.invoiceNumber ?? "—"],
                  ["Direction", DIRECTION_LABEL[doc.direction]],
                  ["Counterparty", doc.counterparty ?? "—"],
                  ["Taxable amount", amount(totals?.taxExclusive)],
                  ["VAT", amount(totals?.taxAmount)],
                  ["Payable", `${amount(totals?.payable)} ${doc.currency ?? ""}`.trim()],
                  ["UUID", <span className="mono">{doc.uuid ?? "—"}</span>],
                ]}
              />
            </div>
          </Card>
          <StagePanel doc={doc} events={events.data ?? []} />
          <Card id="events" title="Events">
            <div className="card-body">
              <EventsBody events={events.data} isError={events.isError} />
            </div>
          </Card>
          <Card id="validation" title="Validation" aside={<ValidationSummary result={validation.data} />}>
            <div className="card-body">
              <ValidationBody result={validation.data} isError={validation.isError} />
            </div>
          </Card>
          <XmlCard docId={docId} doc={doc} />
        </div>
        <div className="grid">
          <Actions doc={doc} />
          <Card id="outcome" title="Outcome">
            <OutcomeBody legs={legs.data} isError={legs.isError} />
          </Card>
          <QrCard docId={docId} />
          <ArchiveCard docId={docId} doc={doc} />
        </div>
      </div>
    </>
  );
}
